// Tradutor de condição jsonb -> texto legível em pt-BR (T7a AC2).
//
// Nunca lança: referência desconhecida vira o próprio código entre aspas mais um aviso, porque a
// tela precisa mostrar *alguma coisa* verdadeira mesmo quando o formulário mudou embaixo da regra.

import {
  CondicaoCatalogo,
  CATALOGO_VAZIO,
  OperadorCatalogo,
  SinalCatalogo,
  VariavelCatalogo,
} from './catalogo'
import {
  Condicao,
  CondicaoAtomica,
  Operador,
  ValorCondicao,
  ehGrupo,
  ehGrupoTodos,
  itensDoGrupo,
  Grupo,
} from './tipos'

export type CodigoAviso =
  | 'CAMPO_INEXISTENTE'
  | 'OPCAO_INEXISTENTE'
  | 'VARIAVEL_DESCONHECIDA'
  | 'SINAL_DESCONHECIDO'
  | 'OPERADOR_DESCONHECIDO'
  | 'CONDICAO_INVALIDA'

export type AvisoReferencia = {
  codigo: CodigoAviso
  referencia: string
  mensagem: string
}

export type CampoDicionario = {
  rotulo: string
  tipo?: string
  /** código da opção -> descrição mostrada ao usuário */
  opcoes?: Record<string, string>
}

export type DicionarioCondicao = {
  campos: Record<string, CampoDicionario>
  catalogo?: CondicaoCatalogo
}

/** Nó da árvore usada para renderizar a condição como lista aninhada (leitor de tela). */
export type NoDescricao = {
  tipo: 'grupo' | 'item'
  /** "TODAS"/"ALGUMA" no grupo; texto legível no item. */
  texto: string
  filhos?: NoDescricao[]
}

export type DescricaoCondicao = {
  texto: string
  arvore: NoDescricao
  avisos: AvisoReferencia[]
}

const SIMBOLO_PADRAO: Record<Operador, string> = {
  '=': '=',
  '!=': '≠',
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
  entre: 'entre',
  em: 'em',
  existe: 'existe',
  nao_existe: 'não existe',
}

const aspas = (texto: string): string => `'${texto}'`

const numero = (valor: unknown): string =>
  typeof valor === 'number' ? String(valor) : String(valor ?? '')

export const descreverCondicao = (
  condicao: Condicao | null | undefined,
  dicionario: DicionarioCondicao = { campos: {} }
): DescricaoCondicao => {
  const avisos: AvisoReferencia[] = []
  if (!condicao || typeof condicao !== 'object') {
    return { texto: '', arvore: { tipo: 'item', texto: '' }, avisos }
  }
  const arvore = descrever(condicao, dicionario, avisos, true)
  return { texto: linearizar(arvore), arvore, avisos }
}

const descrever = (
  condicao: Condicao,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[],
  raiz: boolean
): NoDescricao => {
  if (ehGrupo(condicao)) {
    return grupo(condicao, dicionario, avisos, raiz)
  }
  const atomica = condicao as CondicaoAtomica
  if (!atomica.fonte || !atomica.ref || !atomica.op) {
    avisos.push({
      codigo: 'CONDICAO_INVALIDA',
      referencia: JSON.stringify(condicao),
      mensagem: 'Condição em formato desconhecido',
    })
    return { tipo: 'item', texto: 'condição desconhecida' }
  }
  return { tipo: 'item', texto: item(atomica, dicionario, avisos) }
}

const grupo = (
  condicao: Grupo,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[],
  raiz: boolean
): NoDescricao => {
  const todos = ehGrupoTodos(condicao)
  const filhos = itensDoGrupo(condicao).map((item) => descrever(item, dicionario, avisos, false))
  const prefixo = todos ? 'TODAS' : 'ALGUMA'
  return {
    tipo: 'grupo',
    texto: raiz ? `Quando ${prefixo}` : prefixo,
    filhos,
  }
}

const item = (
  condicao: CondicaoAtomica,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[]
): string => {
  const referencia = descreverReferencia(condicao, dicionario, avisos)
  const simbolo = simboloDoOperador(condicao.op, dicionario.catalogo, avisos)

  if (condicao.op === 'existe' || condicao.op === 'nao_existe') {
    return `${referencia} ${simbolo}`
  }
  return `${referencia} ${simbolo} ${descreverValor(condicao, dicionario, avisos)}`
}

const descreverReferencia = (
  condicao: CondicaoAtomica,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[]
): string => {
  if (condicao.fonte === 'campo') {
    const campo = dicionario.campos?.[condicao.ref]
    if (!campo) {
      avisos.push({
        codigo: 'CAMPO_INEXISTENTE',
        referencia: condicao.ref,
        mensagem: `O campo ${aspas(condicao.ref)} não existe no formulário vinculado`,
      })
      return `resposta ${aspas(condicao.ref)}`
    }
    return `resposta ${aspas(campo.rotulo)}`
  }
  if (condicao.fonte === 'variavel') {
    const variavel = buscarVariavel(condicao.ref, dicionario)
    if (!variavel) {
      avisos.push({
        codigo: 'VARIAVEL_DESCONHECIDA',
        referencia: condicao.ref,
        mensagem: `A variável ${aspas(condicao.ref)} não está no catálogo`,
      })
      return aspas(condicao.ref)
    }
    return variavel.rotulo
  }
  const sinal = buscarSinal(condicao.ref, dicionario)
  if (!sinal) {
    avisos.push({
      codigo: 'SINAL_DESCONHECIDO',
      referencia: condicao.ref,
      mensagem: `O sinal vital ${aspas(condicao.ref)} não está no catálogo`,
    })
    return aspas(condicao.ref)
  }
  return sinal.descricao
}

const descreverValor = (
  condicao: CondicaoAtomica,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[]
): string => {
  const { valor } = condicao
  if (condicao.op === 'entre' && Array.isArray(valor)) {
    return `${valorSimples(condicao, valor[0], dicionario, avisos)} e ${valorSimples(
      condicao,
      valor[1],
      dicionario,
      avisos
    )}`
  }
  if (condicao.op === 'em' && Array.isArray(valor)) {
    return valor.map((item) => valorSimples(condicao, item, dicionario, avisos)).join(', ')
  }
  return valorSimples(condicao, valor as string | number, dicionario, avisos)
}

const valorSimples = (
  condicao: CondicaoAtomica,
  valor: ValorCondicao | undefined,
  dicionario: DicionarioCondicao,
  avisos: AvisoReferencia[]
): string => {
  if (valor === undefined || valor === null) {
    return ''
  }
  if (condicao.fonte === 'campo') {
    const campo = dicionario.campos?.[condicao.ref]
    const opcoes = campo?.opcoes
    if (opcoes && typeof valor === 'string') {
      const descricao = opcoes[valor]
      if (descricao) {
        return aspas(descricao)
      }
      if (Object.keys(opcoes).length > 0) {
        avisos.push({
          codigo: 'OPCAO_INEXISTENTE',
          referencia: valor,
          mensagem: `A opção ${aspas(valor)} não existe no campo ${aspas(campo.rotulo)}`,
        })
      }
    }
    return typeof valor === 'number' ? numero(valor) : aspas(String(valor))
  }
  if (condicao.fonte === 'variavel') {
    const variavel = buscarVariavel(condicao.ref, dicionario)
    const permitido = variavel?.valoresPermitidos?.find((item) => item.valor === String(valor))
    if (permitido) {
      return aspas(permitido.rotulo)
    }
    return typeof valor === 'number' ? numero(valor) : aspas(String(valor))
  }
  const sinal = buscarSinal(condicao.ref, dicionario)
  const unidade = sinal?.unidade ? ` ${sinal.unidade}` : ''
  return `${numero(valor)}${unidade}`
}

const simboloDoOperador = (
  operador: Operador,
  catalogo: CondicaoCatalogo | undefined,
  avisos: AvisoReferencia[]
): string => {
  const doCatalogo = (catalogo ?? CATALOGO_VAZIO).operadores?.find(
    (item: OperadorCatalogo) => item.json === operador
  )
  if (doCatalogo?.simbolo) {
    return doCatalogo.simbolo
  }
  const padrao = SIMBOLO_PADRAO[operador]
  if (!padrao) {
    avisos.push({
      codigo: 'OPERADOR_DESCONHECIDO',
      referencia: String(operador),
      mensagem: `Operador ${aspas(String(operador))} desconhecido`,
    })
    return String(operador)
  }
  return padrao
}

const buscarVariavel = (
  codigo: string,
  dicionario: DicionarioCondicao
): VariavelCatalogo | undefined =>
  (dicionario.catalogo ?? CATALOGO_VAZIO).variaveis?.find((item) => item.codigo === codigo)

const buscarSinal = (codigo: string, dicionario: DicionarioCondicao): SinalCatalogo | undefined =>
  (dicionario.catalogo ?? CATALOGO_VAZIO).sinais?.find((item) => item.codigo === codigo)

/** Versão linear (uma linha), usada no diff e em `title`. */
export const linearizar = (no: NoDescricao): string => {
  if (no.tipo === 'item') {
    return no.texto
  }
  const conector = no.texto.includes('TODAS') ? ' e ' : ' ou '
  const filhos = (no.filhos ?? [])
    .map((filho) => (filho.tipo === 'grupo' ? `(${linearizar(filho)})` : filho.texto))
    .join(conector)
  return `${no.texto}: ${filhos}`
}

/** Atalho para montar o dicionário a partir dos campos de um formulário vinculado. */
export const dicionarioDeCampos = (
  campos: Array<{
    codigo: string
    rotulo: string
    tipo?: string
    opcoes?: Array<{ codigo: string; descricao: string }>
  }>,
  catalogo?: CondicaoCatalogo
): DicionarioCondicao => ({
  campos: campos.reduce<Record<string, CampoDicionario>>((acumulado, campo) => {
    acumulado[campo.codigo] = {
      rotulo: campo.rotulo,
      tipo: campo.tipo,
      opcoes: (campo.opcoes ?? []).reduce<Record<string, string>>((mapa, opcao) => {
        mapa[opcao.codigo] = opcao.descricao
        return mapa
      }, {}),
    }
    return acumulado
  }, {}),
  catalogo,
})
